/**
 * Where a series comes from.
 *
 * Spot and USD-M futures are both Binance and quote the same pair at different
 * prices (basis, funding), so they are stored and charted as separate datasets
 * rather than merged. `equity` is a different provider entirely — daily stock
 * and ETF bars — and rides the same machinery precisely because a candle is a
 * candle: the chart, the indicators and the engine don't care where the OHLCV
 * came from.
 *
 * It exists mainly so a crypto strategy can be measured against something other
 * than itself. "Did this beat holding BTC" and "did this beat holding SPY" are
 * different questions, and only the second one is the one an allocator asks.
 */
export const MARKET_TYPES = ["spot", "futures", "equity", "forex"];
// Both are type guards rather than plain booleans so a check narrows the
// market type for the caller — which is what stops a Binance-only URL map
// from ever being indexed with "equity".
export function isCrypto(marketType) {
    return marketType === "spot" || marketType === "futures";
}
/**
 * Markets served by the external (non-Binance) provider. Daily bars only, and
 * they trade on a calendar with weekends and holidays in it — which is why the
 * analytics resample tolerates missing days rather than assuming a bar per day.
 */
export function isExternal(marketType) {
    return marketType === "equity" || marketType === "forex";
}
export const DEFAULT_MARKET_TYPE = "spot";
/**
 * Who supplied the bars. Two providers can quote the same symbol at slightly
 * different prices, so a backtest names its provider: a machine that imports
 * the file fetches the same series rather than one that merely shares a name.
 *
 * Today the provider follows from the market type (`providerFor`). It is
 * written anyway, so that adding a second crypto or equity provider is a new
 * value here rather than a change to every file already on someone's disk.
 */
export const DATA_PROVIDERS = ["binance", "yahoo"];
/** The provider a market type is served by when nothing more specific is said. */
export function providerFor(marketType) {
    return isExternal(marketType) ? "yahoo" : "binance";
}
/** Runtime check for a provider name read from a file. */
export function isDataProvider(value) {
    return typeof value === "string" && DATA_PROVIDERS.includes(value);
}
const PROVIDER_LABELS = { binance: "Binance", yahoo: "Yahoo Finance" };
/** A provider's display name; an unknown one (from a newer build) shows as written. */
export function providerLabel(provider) {
    return isDataProvider(provider) ? PROVIDER_LABELS[provider] : provider;
}
/** Stable key for a dataset — used as a map key and as a `<select>` value. */
export function sourceKey(source) {
    return `${source.symbol}|${source.timeframe}|${source.marketType}`;
}
/**
 * Inverse of `sourceKey`.
 *
 * Total by construction: the input is a `<select>` value or a URL segment, so a
 * malformed one is a caller bug rather than a user's. Missing parts become
 * empty strings and an unrecognised market type becomes the default, which
 * fails visibly at the query rather than throwing somewhere unrelated.
 */
export function parseSourceKey(key) {
    const parts = key.split("|");
    const marketType = parts[2];
    return {
        symbol: parts[0] ?? "",
        timeframe: parts[1] ?? "",
        marketType: isMarketType(marketType) ? marketType : DEFAULT_MARKET_TYPE,
    };
}
/** Runtime check for a value arriving from outside — a file, a query string. */
export function isMarketType(value) {
    return typeof value === "string" && MARKET_TYPES.includes(value);
}
/** Binance's fixed 8-hourly funding schedule. */
export const FUNDING_INTERVAL_MS = 8 * 60 * 60 * 1000;
export const TIMEFRAMES = [
    "1m",
    "3m",
    "5m",
    "15m",
    "30m",
    "1h",
    "2h",
    "4h",
    "6h",
    "8h",
    "12h",
    "1d",
    "3d",
    "1w",
];
export const TIMEFRAME_MS = {
    "1m": 60_000,
    "3m": 3 * 60_000,
    "5m": 5 * 60_000,
    "15m": 15 * 60_000,
    "30m": 30 * 60_000,
    "1h": 60 * 60_000,
    "2h": 2 * 60 * 60_000,
    "4h": 4 * 60 * 60_000,
    "6h": 6 * 60 * 60_000,
    "8h": 8 * 60 * 60_000,
    "12h": 12 * 60 * 60_000,
    "1d": 24 * 60 * 60_000,
    "3d": 3 * 24 * 60 * 60_000,
    "1w": 7 * 24 * 60 * 60_000,
};
//# sourceMappingURL=market.js.map